import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { api } from '../lib/api';

export default function Reseller() {
  return (
    <div className="space-y-4">
      <div className="flex gap-3 border-b border-border">
        <NavLink to="tenants" className={({isActive}) => `px-3 py-2 ${isActive ? 'border-b-2 border-primary' : 'text-textSecondary'}`}>Tenants</NavLink>
        <NavLink to="billing" className={({isActive}) => `px-3 py-2 ${isActive ? 'border-b-2 border-primary' : 'text-textSecondary'}`}>Billing</NavLink>
        <NavLink to="health"  className={({isActive}) => `px-3 py-2 ${isActive ? 'border-b-2 border-primary' : 'text-textSecondary'}`}>Health</NavLink>
      </div>
      <Routes>
        <Route index element={<Navigate to="tenants" replace />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="billing" element={<Billing />} />
        <Route path="health"  element={<Health />} />
      </Routes>
    </div>
  );
}

function Tenants() {
  const { data: tenants = [] } = useQuery({ queryKey: ['reseller-tenants'], queryFn: () => api.reseller.tenants() });
  return (
    <div className="card p-0 overflow-hidden">
      <table className="table">
        <thead><tr><th>Tenant</th><th>Status</th><th>Extensions</th><th>Trunks</th><th>Calls (30d)</th><th>Minutes (30d)</th><th>Avg MOS</th></tr></thead>
        <tbody>
          {(tenants as any[]).map((t: any) => (
            <tr key={t.domain_uuid} className="hover:bg-elevated">
              <td>{t.domain_description || t.domain_name}</td>
              <td>{t.domain_enabled === 'true' ? <span className="badge-up">enabled</span> : <span className="badge-down">disabled</span>}</td>
              <td>{t.active_extensions}</td>
              <td>{t.trunks}</td>
              <td>{t.calls_30d}</td>
              <td>{t.minutes_30d}</td>
              <td className="font-mono">{t.avg_mos_30d || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Billing() {
  const month = new Date().toISOString().slice(0, 7);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  const { data } = useQuery({ queryKey: ['reseller-billing', month], queryFn: () => api.reseller.billing(month) });
  const tenants = (data as any)?.tenants || [];
  const total = tenants.reduce((s: number, t: any) => s + (t.estimated_charge || 0), 0);

  // Month boundaries for the cost-by-user query
  const monthFrom = `${month}-01`;
  const monthTo   = new Date(new Date(`${month}-01`).getTime() + 32 * 86400000)
    .toISOString().slice(0, 7) + '-01';  // first day of next month as exclusive upper bound

  const { data: userCost, isFetching: userCostLoading } = useQuery({
    queryKey: ['twilio-cost-by-user', selectedDomain, month],
    queryFn: () => api.reports.twilioByUser(monthFrom, monthTo, selectedDomain!),
    enabled: !!selectedDomain,
  });
  const userRows: any[]  = (userCost as any)?.rows || [];
  const userNote: string = (userCost as any)?.note || '';

  return (
    <div className="space-y-4">
      <div className="card text-center">
        <div className="text-textSecondary text-sm">Estimated Total — {month}</div>
        <div className="text-4xl font-semibold mt-2">${total.toFixed(2)}</div>
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Tenant</th><th>Extensions</th><th>Calls</th><th>Minutes</th><th>Estimated</th>
              <th className="text-textSecondary text-xs font-normal">Cost by user</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t: any) => (
              <tr
                key={t.domain_uuid}
                className={`hover:bg-elevated cursor-pointer${selectedDomain === t.domain_uuid ? ' bg-elevated' : ''}`}
                onClick={() => setSelectedDomain(prev => prev === t.domain_uuid ? null : t.domain_uuid)}
              >
                <td>{t.domain_description || t.domain_name}</td>
                <td>{t.extensions}</td>
                <td>{t.calls}</td>
                <td>{t.minutes}</td>
                <td>${(t.estimated_charge || 0).toFixed(2)}</td>
                <td className="text-textSecondary text-xs">
                  {selectedDomain === t.domain_uuid ? 'hide' : 'show'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-user cost attribution panel — shown when a tenant row is clicked */}
      {selectedDomain && (
        <div className="card space-y-2">
          <div className="font-medium text-sm">
            Twilio Cost by User — {tenants.find((t: any) => t.domain_uuid === selectedDomain)?.domain_description
              || tenants.find((t: any) => t.domain_uuid === selectedDomain)?.domain_name
              || selectedDomain}
            <span className="text-textSecondary font-normal"> ({month})</span>
          </div>
          {userNote && <div className="text-xs text-textSecondary">{userNote}</div>}
          {userCostLoading ? (
            <div className="text-sm text-textSecondary p-2">Loading…</div>
          ) : !userRows.length ? (
            <div className="text-sm text-textSecondary p-2">No CDR data for this tenant.</div>
          ) : (
            <div className="overflow-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Ext</th><th>User</th><th>Minutes</th><th>Share %</th>
                    <th>Twilio Cost</th><th>Seat Price</th><th>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {userRows.map((r: any) => {
                    const margin: number | null = r.margin;
                    const marginColor = margin === null ? '' : margin >= 0 ? 'text-emerald-500' : 'text-rose-400';
                    return (
                      <tr key={r.extension} className="hover:bg-elevated">
                        <td className="font-mono">{r.extension}</td>
                        <td>{r.user_name}</td>
                        <td className="font-mono">{r.total_minutes}</td>
                        <td className="font-mono">{r.minutes_pct}%</td>
                        <td className="font-mono">${r.attributed_cost != null ? r.attributed_cost.toFixed(2) : '—'}</td>
                        <td className="font-mono">{r.seat_price != null ? `$${(r.seat_price as number).toFixed(2)}` : '—'}</td>
                        <td className={`font-mono font-semibold ${marginColor}`}>
                          {margin !== null ? (margin >= 0 ? '+' : '') + margin.toFixed(2) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Health() {
  const { data } = useQuery({ queryKey: ['reseller-health'], queryFn: () => api.reseller.health(), refetchInterval: 10_000 });
  return (
    <div className="grid grid-cols-3 gap-4">
      <Stat label="CPU" value={(data as any)?.cpu ? `${(data as any).cpu}%` : '—'} />
      <Stat label="RAM" value={(data as any)?.ram ? `${(data as any).ram}%` : '—'} />
      <Stat label="Disk" value={(data as any)?.disk || '—'} />
      <Stat label="Active Calls" value={(data as any)?.fs_active_calls || '—'} />
      <Stat label="Active Registrations" value={(data as any)?.fs_active_regs || '—'} />
      <Stat label="SBC nodes online" value={(data as any)?.sbc_nodes_online ?? '—'} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="card text-center">
      <div className="text-textSecondary text-sm">{label}</div>
      <div className="text-3xl font-semibold mt-2">{value}</div>
    </div>
  );
}
